function captureError(code, message) {
  const error = new Error(message)
  error.code = code
  return error
}

function waitForVideoMetadata(video) {
  if (video.readyState >= 1 && video.videoWidth > 0 && video.videoHeight > 0) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const onLoaded = () => {
      cleanup()
      resolve()
    }
    const onError = () => {
      cleanup()
      reject(captureError('capture-video-error', 'No fue posible leer la captura de la pestaña.'))
    }
    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoaded)
      video.removeEventListener('error', onError)
    }
    video.addEventListener('loadedmetadata', onLoaded, { once: true })
    video.addEventListener('error', onError, { once: true })
  })
}

function waitForFreshFrame(video) {
  return new Promise(resolve => {
    // La interfaz se oculta después de que el usuario autoriza la pestaña. Dar
    // tiempo al stream para emitir otro frame evita capturar el modal anterior.
    window.setTimeout(() => {
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(() => resolve())
      } else {
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve))
      }
    }, 120)
  })
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob)
      else reject(captureError('capture-encode-error', 'No fue posible crear el archivo PNG.'))
    }, 'image/png')
  })
}

/**
 * Captura un frame real de la pestaña elegida por el usuario. Al provenir del
 * stream de pantalla, la imagen no depende de que los canvas de OpenLayers
 * sean origin-clean y conserva controles, HUD y paneles HTML visibles.
 */
export async function captureVisibleBrowserTab({ beforeFrame, afterFrame } = {}) {
  if (!window.isSecureContext) {
    throw captureError(
      'capture-insecure-context',
      'La captura requiere una conexión segura. Abra el visor mediante HTTPS o desde http://localhost.'
    )
  }
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
    throw captureError(
      'capture-unsupported',
      'Este navegador no ofrece captura de pestañas. Utilice una versión actualizada de Chrome o Edge y verifique que el sitio use HTTPS.'
    )
  }

  let stream = null
  let prepared = false
  try {
    // Debe invocarse directamente desde el clic para conservar la activación
    // transitoria exigida por el navegador. Las opciones adicionales son
    // sugerencias; el usuario siempre conserva la elección final de superficie.
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude'
    })

    const track = stream.getVideoTracks()[0]
    if (!track) {
      throw captureError('capture-no-video', 'La fuente seleccionada no contiene video.')
    }
    const displaySurface = track.getSettings && track.getSettings().displaySurface
    if (displaySurface && displaySurface !== 'browser') {
      throw captureError(
        'capture-wrong-surface',
        'Seleccione la pestaña actual del visor, no una ventana ni la pantalla completa.'
      )
    }

    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.srcObject = stream
    await waitForVideoMetadata(video)
    await video.play()

    if (beforeFrame) {
      await beforeFrame()
      prepared = true
    }
    await waitForFreshFrame(video)

    if (!video.videoWidth || !video.videoHeight) {
      throw captureError('capture-empty-frame', 'La captura de la pestaña está vacía.')
    }
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const context = canvas.getContext('2d')
    if (!context) {
      throw captureError('capture-no-context', 'No fue posible preparar la imagen de captura.')
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    return await canvasToPngBlob(canvas)
  } finally {
    if (stream) stream.getTracks().forEach(track => track.stop())
    if (prepared && afterFrame) await afterFrame()
  }
}
